import createFuzzySearch from '@nozbe/microfuzz'

/**
 * 
 *  Topic filtering functionality
 * 
 */
export class Glossary {

    /**
     * 
     * Contructor
     * 
     * @param {element} list - The list element to render the suggestions into
     * @param {element} input - The input element to get the user query from
     * @param {element} element - The suggestion box element
     */
    constructor(grid, input, letters, clear_filters) {

        // Needs and wants
        this.query = '';
        this.glossary_terms = window.glossary_terms;
        this.filtered_glossary_terms = [];
        this.selected_letters = [];
        this.grid = grid;
        this.input = input;
        this.letters = letters;
        this.clear_filters = clear_filters;
        this.maximum_score = 4;
        this.search = createFuzzySearch(this.glossary_terms, { key: 'title' });

        // Initialise the suggestion box interactions
        this.init();

    }

    /**
     * 
     * Initialise the suggestion box interactions
     * 
     */
    init() {

        // Attach keyboard events to the input field
        this.input.addEventListener('keyup', () => {
            // Set a timeout on the filter section 
            setTimeout(() => this.filter(), 50);
        });

        // Fetch the list of topics on initial focus
        this.clear_filters.addEventListener('click', (event) => {
            event.preventDefault();
            this.clear();
        });

        // Attach click events to the letters
        this.letters.forEach(letter => {
            letter.addEventListener('click', event => {
                event.preventDefault();
                this.toggleLetter(letter);
            });
        });

        // Attach click events to the see all links on the letters
        this.grid.addEventListener('click', event => {
            if (event.target.classList.contains('js-pick-letter')) {
                event.preventDefault();
                this.letters.forEach(letter => {
                    if (letter.dataset.letter === event.target.dataset.letter) this.toggleLetter(letter);
                })
            }
        });



    }

    clear() {
        this.query = '';
        this.input.value = '';
        this.selected_letters = [];
        this.letters.forEach(letter => letter.classList.remove('active'));

        // Set a timeout on the filter section 
        setTimeout(() => this.filter(), 50);
    }




    /**
    * 
    * Toggle letters on and off
    * 
    * @param {node} letter 
    */
    toggleLetter(letter) {

        if (letter.classList.contains('active')) {
            letter.classList.remove('active');
            this.selected_letters = this.selected_letters.filter(item => item !== letter.dataset.letter);
        } else {
            letter.classList.add('active');
            this.selected_letters.push(letter.dataset.letter);
        }

        // Set a timeout on the filter section 
        setTimeout(() => this.filter(), 50);
    }



    /**
     * 
     * Filter a list of predefined search terms based on user input
     * 
     */
    filter() {

        // Update the query to match what a user has typed
        this.query = this.input.value.trim();

        // Filter the list of topics based on the query
        if (this.query.length) {
            this.filtered_glossary_terms = this.search(this.query);
        }
        // If there is no query then we need to reset the filtered topics
        else {
            this.filtered_glossary_terms = this.glossary_terms.map(topic => { return { item: topic, score: 0 } })
        }

        // Filter the list of topics based on the selected letters intersecting with the first letter of the topic
        if (this.selected_letters.length > 0) {
            this.filtered_glossary_terms = this.filtered_glossary_terms.filter(item => this.selected_letters.includes(item.item.title.charAt(0)));
        }

        // Show/hide the clear filters button bsed on search parameters being present
        this.clear_filters.classList[this.query.length || this.selected_letters.length > 0 ? 'add' : 'remove']('active');

        // Render the list of suggestions
        this.render();

    }


    /**
     * 
     * Render out the list of suggestions
     * 
     * @param {string} html - HTML template initialiser
     */
    render(html = '') {

        // Group the topics by their first letter in an array of arrays instead an object of arrays
        let grouped_topics = this.filtered_glossary_terms.reduce((r, a) => {
            r[a.item.title.charAt(0)] = [...r[a.item.title.charAt(0)] || [], a];
            return r;
        }, {});

        // order the grouped topics by their first letter
        grouped_topics = Object.keys(grouped_topics).sort().reduce(
            (obj, key) => {
                obj[key] = grouped_topics[key];
                return obj;
            },
            {}
        );

        // Loop through the grouped topics and render them out
        for (const [letter, items] of Object.entries(grouped_topics)) {
            if (letter) html += this.template(letter, items)
        }

        // Render the HTML
        this.grid.innerHTML = (html === '') ? this.empty() : html;

    }

    /**
     * 
     * Mark a searched term in the list of topics
     * 
     * @param {string} name 
     * @returns {string} template 
     */
    marked(name) {
        let regex = new RegExp(this.query.split(' ').map(word => `(${word})`).join('|'), 'gi');
        return name.replace(regex, `<span class="marked">$&</span>`);
    }

    /**
     * 
     * Return a single topic item template
     * 
     * @param {object} item 
     * @returns 
     */
    item(item) {
        return /*html*/`
        <div class="glossary__wrap__grid__letter__list__item">
            <h4><b>${this.marked(item.item.title)}</b></h4>
            <hr>
            <p>${this.marked(item.item.defition.replace(/&lt;p&gt;/g, '').replace(/&lt;\/p&gt;/g, ''))}</p>
        </div>`;
    }


    /**
     * 
     * No results template
     * 
     * @returns {string} template
     */
    empty() {
        return /*html*/`
        <div class="topics__wrap__grid__column topics__wrap__grid__column--full">
            <div class="topics__wrap__grid__column__heading">
                <h3 class="h2">No results found</h3>
                <hr>
            </div>
        </div>`;
    }

    /**
     * 
     * Single item template
     * 
     * @param {*} letter 
     * @param {*} items 
     * @param {*} html 
     * @returns 
     */
    template(letter, items, html = '') {
        // Return an empty string if our items are all above the maximum score
        if (!items.some(item => item.score < this.maximum_score)) return ``;

        return /*html*/`
        <div class="glossary__wrap__grid__letter" data-letter="${letter.toUpperCase()}">
            <div class="glossary__wrap__grid__column__heading">
                <h3 class="h2">${letter.toUpperCase()}</h3>
                <hr>
            </div>
            <div class="glossary__wrap__grid__letter__list">
                ${items.map(item => (item.score < this.maximum_score) ? this.item(item) : null).join('')}
            </div>
        </div>`;
    }

}